import React, { useState } from 'react';
import { CheckCircle2Icon, ClockIcon, FacebookIcon, MailIcon, MapPinIcon, PhoneIcon, ShieldCheckIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { company } from '../../data/company';
import { services } from '../../data/services';
import { cities, extraAreas } from '../../data/cities';
import { SectionHeading } from '../ui/SectionHeading';
type Status = 'idle' | 'sending' | 'success';
type Errors = Partial<Record<'name' | 'phone' | 'email' | 'service', string>>;
const fieldClass = 'mt-1.5 w-full rounded-md border border-navy/20 bg-white px-3.5 py-3 text-base text-ink placeholder:text-ink-muted/70 transition-colors duration-150 ease-smooth focus:border-brand-700 focus:outline-none';
export function ContactSection() {
  const {
    t,
    b,
    lang
  } = useLanguage();
  const [status, setStatus] = useState<Status>('idle');
  const [errors, setErrors] = useState<Errors>({});
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const next: Errors = {};
    const name = String(data.get('name') ?? '').trim();
    const phone = String(data.get('phone') ?? '').trim();
    const email = String(data.get('email') ?? '').trim();
    const service = String(data.get('service') ?? '');
    if (name.length < 2) next.name = t('contact.errName');
    if (phone.replace(/\D/g, '').length < 10) next.phone = t('contact.errPhone');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) next.email = t('contact.errEmail');
    if (!service) next.service = t('contact.errService');
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setStatus('sending');
    window.setTimeout(() => {
      setStatus('success');
      form.reset();
    }, 900);
  };
  return <section id="contact" className="scroll-mt-24 bg-white py-20 md:py-24" aria-labelledby="contact-title" data-id="element-184" data-mp="r3vy6ne9wih9">
      <div className="mx-auto max-w-page px-4 sm:px-6 lg:px-8" data-id="element-185" data-mp="w1nlt57vxifn">
        <SectionHeading id="contact-title" title={t('contact.title')} subtitle={t('contact.sub')} data-id="element-186" data-mp="2s5k9lxwf20h0" />

        <div className="mt-14 grid gap-10 lg:grid-cols-[1.15fr_1fr] lg:gap-14" data-id="element-187" data-mp="2o3yqhutagpyd">
          <div className="rounded-lg border border-navy/10 bg-mist p-6 md:p-8" data-id="element-188" data-mp="31uvh5g3nmx2s">
            {status === 'success' ? <div className="flex h-full flex-col items-start justify-center py-10" data-id="element-189" data-mp="2x2kei5hj4x6j">
                <CheckCircle2Icon className="h-12 w-12 text-brand-700" aria-hidden="true" data-id="element-190" data-mp="3brqc5p0pyfcb" />
                <h3 className="mt-5 font-display text-2xl font-bold text-navy" data-id="element-191" data-mp="36tyd8b1npsym">{t('contact.successTitle')}</h3>
                <p className="mt-3 max-w-md text-base text-ink-muted" data-id="element-192" data-mp="3ks50kx8zcak1">{t('contact.successBody')}</p>
                <button type="button" onClick={() => setStatus('idle')} className="mt-7 rounded-md border-2 border-navy px-5 py-3 text-sm font-bold text-navy transition-colors duration-200 ease-smooth hover:bg-navy hover:text-white" data-id="element-193" data-mp="3fv68ek8vmdoh">
                  {t('contact.another')}
                </button>
              </div> : <form onSubmit={handleSubmit} noValidate className="grid gap-5 sm:grid-cols-2" data-id="element-194" data-mp="5vt81pub6f40">
                <div className="sm:col-span-2" data-id="element-195" data-mp="3oa2jkiqsfjzu">
                  <label htmlFor="name" className="text-sm font-semibold text-navy" data-id="element-196" data-mp="3o9aulqrp4kij">
                    {t('contact.name')} <span className="text-cta-dark" data-id="element-197" data-mp="3ecxry3e6awbn">*</span>
                  </label>
                  <input id="name" name="name" type="text" autoComplete="name" required aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'name-error' : undefined} className={fieldClass} data-id="element-198" data-mp="3ef0pr9lkhtqx" />
                  {errors.name && <p id="name-error" className="mt-1.5 text-sm font-medium text-cta-dark" data-id="element-199" data-mp="17m84h5sda3it">
                      {errors.name}
                    </p>}
                </div>

                <div data-id="element-200" data-mp="dwr40g2auq36">
                  <label htmlFor="phone" className="text-sm font-semibold text-navy" data-id="element-201" data-mp="dwhesup2wmyj">
                    {t('contact.phone')} <span className="text-cta-dark" data-id="element-202" data-mp="8wmoas97tze0">*</span>
                  </label>
                  <input id="phone" name="phone" type="tel" autoComplete="tel" required aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'phone-error' : undefined} className={fieldClass} data-id="element-203" data-mp="3z30b0mpu88v" />
                  {errors.phone && <p id="phone-error" className="mt-1.5 text-sm font-medium text-cta-dark" data-id="element-204" data-mp="3hesiv966p12u">
                      {errors.phone}
                    </p>}
                </div>

                <div data-id="element-205" data-mp="38e2swm3lu4x1">
                  <label htmlFor="email" className="text-sm font-semibold text-navy" data-id="element-206" data-mp="38dkx1qkutspn">
                    {t('contact.email')} <span className="text-cta-dark" data-id="element-207" data-mp="1nwtkewv7qzcj">*</span>
                  </label>
                  <input id="email" name="email" type="email" autoComplete="email" required aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} className={fieldClass} data-id="element-208" data-mp="1ruixwb4lkm6b" />
                  {errors.email && <p id="email-error" className="mt-1.5 text-sm font-medium text-cta-dark" data-id="element-209" data-mp="1e4xmblt4n98b">
                      {errors.email}
                    </p>}
                </div>

                <div data-id="element-210" data-mp="lg9vzbtittnw">
                  <label htmlFor="service" className="text-sm font-semibold text-navy" data-id="element-211" data-mp="lgs2r2dt7qa2">
                    {t('contact.service')} <span className="text-cta-dark" data-id="element-212" data-mp="qel5flfwm25l">*</span>
                  </label>
                  <select id="service" name="service" required defaultValue="" aria-invalid={Boolean(errors.service)} aria-describedby={errors.service ? 'service-error' : undefined} className={fieldClass} data-id="element-213" data-mp="ie5dl643rtik">
                    <option value="" data-id="element-214" data-mp="n7q3sbzgfj77">{t('contact.select')}</option>
                    {services.map(service => <option key={service.slug} value={service.slug} data-id="element-215" data-mp="3rjloaozrbzzf">
                        {b(service.title)}
                      </option>)}
                  </select>
                  {errors.service && <p id="service-error" className="mt-1.5 text-sm font-medium text-cta-dark" data-id="element-216" data-mp="4dul5yn5a6va">
                      {errors.service}
                    </p>}
                </div>

                <div data-id="element-217" data-mp="1fszsxh24uvlr">
                  <label htmlFor="city" className="text-sm font-semibold text-navy" data-id="element-218" data-mp="1ftjdl5j63y3h">
                    {t('contact.location')}
                  </label>
                  <select id="city" name="city" defaultValue="" className={fieldClass} data-id="element-219" data-mp="32rads9opm4fw">
                    <option value="" data-id="element-220" data-mp="2or9davccwz0y">{t('contact.select')}</option>
                    {cities.map(city => <option key={city.slug} value={city.slug} data-id="element-221" data-mp="2tpkopaiavhla">
                        {city.name}, WA
                      </option>)}
                    {extraAreas.map(area => <option key={area} value={area} data-id="element-222" data-mp="3lhie6k10ygse">
                        {area}, WA
                      </option>)}
                  </select>
                </div>

                <div className="sm:col-span-2" data-id="element-223" data-mp="3co59ylxifsr0">
                  <label htmlFor="message" className="text-sm font-semibold text-navy" data-id="element-224" data-mp="3cmu6kn7z8n0f">
                    {t('contact.message')}
                  </label>
                  <textarea id="message" name="message" rows={4} placeholder={t('contact.messagePlaceholder')} className={fieldClass} data-id="element-225" data-mp="903p2w16zd6y" />
                </div>

                <fieldset className="sm:col-span-2" data-id="element-226" data-mp="1vcx749lums2p">
                  <legend className="text-sm font-semibold text-navy" data-id="element-227" data-mp="1vdgewxhsc030">{t('contact.langPref')}</legend>
                  <div className="mt-2 flex gap-6" data-id="element-228" data-mp="1qfx2820qjvd6">
                    {(['en', 'es'] as const).map(option => <label key={option} className="flex items-center gap-2 text-sm text-ink-soft" data-id="element-229" data-mp="2nzhznde29a7u">
                        <input type="radio" name="langPref" value={option} defaultChecked={lang === option} className="h-4 w-4 accent-[#1B6EA8]" data-id="element-230" data-mp="2j0ycj9nqdsdw" />
                        {option === 'en' ? 'English' : 'Español'}
                      </label>)}
                  </div>
                </fieldset>

                <div className="sm:col-span-2" data-id="element-231" data-mp="3abuh8ne9qaay">
                  <button type="submit" disabled={status === 'sending'} className="w-full rounded-md bg-cta px-6 py-4 text-base font-bold text-white transition-colors duration-200 ease-smooth hover:bg-cta-dark disabled:cursor-not-allowed disabled:opacity-70" data-id="element-232" data-mp="3eblcb3atvcla">
                    {status === 'sending' ? t('contact.sending') : t('cta.scheduleInspection')}
                  </button>
                  <p className="mt-3 flex items-center gap-2 text-xs text-ink-muted" data-id="element-233" data-mp="1smxyz7d2nlx">
                    <ShieldCheckIcon className="h-4 w-4 shrink-0 text-brand-700" aria-hidden="true" data-id="element-234" data-mp="5wktz11e3iar" />
                    {t('contact.privacy')}
                  </p>
                </div>
              </form>}
          </div>

          <div data-id="element-235" data-mp="2cmj0nty9chxd">
            <h3 className="font-display text-xl font-bold text-navy" data-id="element-236" data-mp="2clzgs7p1rjgp">{t('contact.infoTitle')}</h3>
            <ul className="mt-6 space-y-5" data-id="element-237" data-mp="2zer7prmsolvi">
              <li className="flex gap-3" data-id="element-238" data-mp="2zg2abo4i7nl1">
                <PhoneIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-700" aria-hidden="true" data-id="element-239" data-mp="34dluf4nvuukw" />
                <div data-id="element-240" data-mp="39covke0qsgnl">
                  <p className="text-sm font-semibold text-navy" data-id="element-241" data-mp="39cfbt86aj5qc">{t('cta.call')}</p>
                  <a href={company.phoneHref} className="text-base font-bold text-brand-700 hover:text-brand-800" data-id="element-242" data-mp="3ebi3zs169hgw">
                    {company.phone}
                  </a>
                </div>
              </li>
              <li className="flex gap-3" data-id="element-243" data-mp="3nboh3csykffj">
                <MailIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-700" aria-hidden="true" data-id="element-244" data-mp="3nan6tajrnwyv" />
                <div data-id="element-245" data-mp="3s3yd9ako2bq9">
                  <p className="text-sm font-semibold text-navy" data-id="element-246" data-mp="3s5j0ef4nise3">Email</p>
                  <a href={company.emailHref} className="break-all text-base text-ink-soft hover:text-brand-700" data-id="element-247" data-mp="w3qh8b8e7pr">
                    {company.email}
                  </a>
                </div>
              </li>
              <li className="flex gap-3" data-id="element-248" data-mp="9vh3gaykdef8">
                <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-700" aria-hidden="true" data-id="element-249" data-mp="1ug42mb69b4rc" />
                <div data-id="element-250" data-mp="1qdr7egp4yp8t">
                  <p className="text-sm font-semibold text-navy" data-id="element-251" data-mp="1qd7pv13ee2vb">{t('contact.serviceArea')}</p>
                  <p className="text-base text-ink-soft" data-id="element-252" data-mp="1lfpcmyicl1hi">{t('contact.serviceAreaValue')}</p>
                </div>
              </li>
              <li className="flex gap-3" data-id="element-253" data-mp="1ghxmoa19jpfy">
                <ClockIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-700" aria-hidden="true" data-id="element-254" data-mp="2e43b1fdclztl" />
                <div data-id="element-255" data-mp="293yvpso6fb4y">
                  <p className="text-sm font-semibold text-navy" data-id="element-256" data-mp="295tfv4rmiz7z">{t('contact.hours')}</p>
                  <p className="text-base text-ink-soft" data-id="element-257" data-mp="24cqjkovgpbo3">{b(company.hours)}</p>
                </div>
              </li>
              <li className="flex gap-3" data-id="element-258" data-mp="1zdvzljlrdh2e">
                <ShieldCheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-700" aria-hidden="true" data-id="element-259" data-mp="2wu2vfoy8uqqo" />
                <div data-id="element-260" data-mp="2s3cpa9hzujux">
                  <p className="text-sm font-semibold text-navy" data-id="element-261" data-mp="2s32zalwy8cp8">License</p>
                  <p className="text-base text-ink-soft" data-id="element-262" data-mp="q62kk5d5cgaj">{company.license}</p>
                </div>
              </li>
            </ul>

            <iframe title={t('contact.mapTitle')} src="https://www.openstreetmap.org/export/embed.html?bbox=-124.2%2C46.6%2C-121.6%2C47.9&layer=mapnik&marker=47.2151%2C-123.1007" className="mt-8 h-72 w-full rounded-lg border border-navy/10" loading="lazy" data-id="element-263" data-mp="u6cqji0xv6pe" />

            <div className="mt-6" data-id="element-264" data-mp="6gicix4vvc3k">
              <p className="text-sm font-semibold text-navy" data-id="element-265" data-mp="bgoa0txu3stk">{t('contact.follow')}</p>
              <a href={company.facebook} target="_blank" rel="noreferrer noopener" aria-label="Facebook" className="mt-2 inline-flex h-10 w-10 items-center justify-center rounded-md border border-navy/15 text-navy transition-colors duration-150 ease-smooth hover:border-brand-700 hover:text-brand-700" data-id="element-266" data-mp="bejypqxlvrj9">
                <FacebookIcon className="h-5 w-5" aria-hidden="true" data-id="element-267" data-mp="1wrwqy263dggh" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}